import { useContext, useMemo } from "react";
import ReportDowntimeTopFiveBarChart from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChart";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { DefaultTranslationFieldsInTable, DisplayInOptionsEnum } from "constants/constants";
import { IChartBar } from "components/ReportDowntimeTopFiveBarChart/ReportDowntimeTopFiveBarChartInterfaces";
import NoData from "components/NoData/NoData.view";
import { CircularProgress } from "@mui/material";
import { useStyles } from "../DowntimeSummaryByFailureType/DowntimeSummaryByFailureType.styles";
import { MillDashboardContext } from "contexts/MillDashboardsContext/MillDashboardsContext";
import { IMillReportCardProps } from "../../../MillDashboardInterfaces";
import { useTranslation } from "react-i18next";
import { translateDefaultEntity } from "utilities/localesUtilities";

const TopFiveSecondaryFailureCodes = ({ countryCode }: IMillReportCardProps) => {
    const classes = useStyles();
    const { millId } = useParams();
    const { i18n } = useTranslation();
    const {
        selectedFailureType,
        selectedFailureCode,
        selectedSecondaryFailureCode,
        handleSetSelectedSecondaryFailureCode,
        filters,
        totalDowntimeSeconds,
    } = useContext(SummaryReportDashboardContext);
    const {
        displayUnit: displayIn,
        reportRangeEndDate: endDate,
        reportRangeStartDate: startDate,
    } = useContext(MillDashboardContext);

    const { downtimeSummaryBySecondaryFailureCodeTotal, loading } =
        MillsEntity.GetDowntimeSummaryTotalsBySecondaryFailureCodeMillDashboard(
            { ...filters, endDate, startDate, displayIn },
            +millId!,
            Number(selectedFailureCode?.id),
        );

    const downtimeSummaryFormatted = useMemo(
        () =>
            downtimeSummaryBySecondaryFailureCodeTotal.map((obj) => ({
                id: obj.failure_data.failure_code_id,
                name: obj.failure_data.name,
                total_occurrences: obj.total_occurrences,
                total_seconds: obj.total_seconds,
                default_failure_code_translations: obj.failure_data.default_failure_code_translations,
            })),
        [downtimeSummaryBySecondaryFailureCodeTotal],
    );

    const graphData: IChartBar[] = useMemo(
        () =>
            downtimeSummaryFormatted
                .sort((a, b) =>
                    displayIn === DisplayInOptionsEnum.MINUTES || displayIn === DisplayInOptionsEnum.PERCENT
                        ? b.total_seconds - a.total_seconds
                        : b.total_occurrences - a.total_occurrences,
                )
                .slice(0, 5)
                .reverse()
                .map((failureCode) => {
                    const secondsToMinutes =
                        Math.round(failureCode.total_seconds / 60) < 1 ? 1 : Math.round(failureCode.total_seconds / 60);

                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return {
                        id: defaultFailureCodeTranslatedName,
                        countryCode: countryCode!,
                        [`${defaultFailureCodeTranslatedName}Color`]: selectedFailureType?.color_code || "",
                        [defaultFailureCodeTranslatedName]:
                            displayIn === DisplayInOptionsEnum.MINUTES
                                ? secondsToMinutes
                                : displayIn === DisplayInOptionsEnum.PERCENT
                                ? totalDowntimeSeconds
                                    ? (100 * failureCode.total_seconds) / totalDowntimeSeconds
                                    : 0
                                : failureCode.total_occurrences,
                    };
                }),
        [
            downtimeSummaryFormatted,
            displayIn,
            i18n.language,
            countryCode,
            selectedFailureType?.color_code,
            totalDowntimeSeconds,
        ],
    );

    const selectedSecondaryFailureCodeName = useMemo(
        () =>
            selectedSecondaryFailureCode
                ? translateDefaultEntity(
                      i18n.language,
                      selectedSecondaryFailureCode,
                      "name",
                      DefaultTranslationFieldsInTable.FAILURE_CODE,
                  )
                : undefined,
        [selectedSecondaryFailureCode, i18n.language],
    );

    return loading ? (
        <div className={classes.noData}>
            <CircularProgress size={24} color="info" />
        </div>
    ) : graphData.length > 0 ? (
        <ReportDowntimeTopFiveBarChart
            data={graphData}
            index="id"
            noPercent={displayIn !== DisplayInOptionsEnum.PERCENT}
            selectedBar={selectedSecondaryFailureCodeName}
            useExternalSelectedBarProp
            onBarClick={(value) => {
                const secondaryFailureCodeSelected = downtimeSummaryFormatted.find((failureCode) => {
                    const defaultFailureCodeTranslatedName = translateDefaultEntity(
                        i18n.language,
                        failureCode,
                        "name",
                        DefaultTranslationFieldsInTable.FAILURE_CODE,
                    );
                    return defaultFailureCodeTranslatedName === value;
                });
                if (secondaryFailureCodeSelected)
                    handleSetSelectedSecondaryFailureCode({
                        id: secondaryFailureCodeSelected.id,
                        name: secondaryFailureCodeSelected.name,
                        default_failure_code_translations:
                            secondaryFailureCodeSelected.default_failure_code_translations,
                    });
            }}
        />
    ) : (
        <NoData className={classes.noData} />
    );
};

export default TopFiveSecondaryFailureCodes;
